import { TPS_SKY_COLOR_GLSL } from "./atmosphere.js";
import { LIGHTING_CONFIG, LIGHTING_GLSL } from "./lighting.js";
import { WATER_VISUAL_CONFIG } from "./water.js";

const waves = `
uniform float uTime;
uniform highp int uWaveCount;
vec3 waveSample(vec2 position) {
  vec3 result = vec3(0.0);
  ${WATER_VISUAL_CONFIG.waves.map(([amplitude, x, z, frequency, speed, phase], index) => `
  if (uWaveCount > ${index}) {
    float angle = dot(position, vec2(${x.toFixed(4)}, ${z.toFixed(4)})) * ${frequency.toFixed(5)} - uTime * ${speed.toFixed(3)} + ${phase.toFixed(3)};
    float derivative = ${amplitude.toFixed(3)} * ${frequency.toFixed(5)} * cos(angle);
    result += vec3(${amplitude.toFixed(3)} * sin(angle), derivative * ${x.toFixed(4)}, derivative * ${z.toFixed(4)});
  }`).join("\n")}
  return result;
}
float waveDamping(vec2 depthBank) {
  return smoothstep(0.02, 0.5, depthBank.x) * smoothstep(0.0, 0.7, depthBank.y);
}`;

export const WATER_VERTEX_SOURCE = `#version 300 es
precision highp float;
layout(location=0) in vec3 aPosition;
layout(location=1) in vec3 aNormal;
// Reuse the mesh's existing float2 channel: signed depth and distance to bank.
layout(location=9) in vec2 aWaterDepthBank;
uniform mat4 uViewProjection;
out vec3 vWorld;
out vec3 vMeanNormal;
out vec2 vDepthBank;
${waves}
void main() {
  vDepthBank = aWaterDepthBank;
  vMeanNormal = aNormal;
  vWorld = aPosition;
  vWorld.y += waveSample(aPosition.xz).x * waveDamping(aWaterDepthBank);
  gl_Position = uViewProjection * vec4(vWorld, 1.0);
}`;

export const WATER_FRAGMENT_SOURCE = `#version 300 es
precision highp float;
in vec3 vWorld;
in vec3 vMeanNormal;
in vec2 vDepthBank;
uniform vec3 uCamera;
uniform vec3 uViewForward;
uniform vec3 uSunDirection;
uniform vec3 uSunColor;
uniform vec3 uAmbientColor;
uniform vec3 uFogColor;
uniform float uFogNear;
uniform float uFogFar;
uniform float uFogMaximum;
uniform float uTpsAtmosphere;
uniform float uFogEnabled;
uniform float uLightingEnabled;
uniform float uLocalSpecular;
uniform int uRippleCount;
uniform vec4 uRipples[${WATER_VISUAL_CONFIG.maximumRipples}];
out vec4 outColor;
${waves}
${TPS_SKY_COLOR_GLSL}
${LIGHTING_GLSL}

void main() {
  if (vDepthBank.x <= 0.002) discard;
  vec3 wave = waveSample(vWorld.xz) * waveDamping(vDepthBank);
  vec2 gradient = wave.yz;
  float disturbed = 0.0;
  for (int index = 0; index < ${WATER_VISUAL_CONFIG.maximumRipples}; index++) {
    if (index >= uRippleCount) break;
    vec4 impulse = uRipples[index];
    float age = uTime - impulse.z;
    if (age < 0.0 || age > ${WATER_VISUAL_CONFIG.rippleLifetime.toFixed(2)}) continue;
    vec2 delta = vWorld.xz - impulse.xy;
    float distanceSquared = dot(delta, delta);
    if (distanceSquared > 18.0) continue;
    float distance = sqrt(max(0.0001, distanceSquared));
    float ring = distance - age * 1.25 - 0.12;
    float envelope = exp(-ring * ring * 14.0);
    float fade = (1.0 - age / ${WATER_VISUAL_CONFIG.rippleLifetime.toFixed(2)}) * impulse.w;
    gradient += delta / distance * (cos(ring * 12.0) * 0.12 - sin(ring * 12.0) * ring * 0.28) * envelope * fade;
    disturbed += envelope * fade * 0.35;
  }
  vec3 normal = normalize(vMeanNormal + vec3(-gradient.x, 0.0, -gradient.y));
  if (!gl_FrontFacing) normal = -normal;
  vec3 view = normalize(uCamera - vWorld);
  float deep = smoothstep(0.06, 1.65, vDepthBank.x);
  vec3 body = mix(vec3(0.195, 0.245, 0.205), vec3(0.065, 0.140, 0.160), deep);
  vec3 light = vec3(0.82);
  vec3 specular = vec3(0.0);
  if (uLightingEnabled > 0.5) {
    vec3 sun = normalize(uSunDirection);
    float sunVisibility = directionalVisibility(vWorld, normal);
    light = uAmbientColor * 0.82 + uSunColor * max(0.0, dot(normal, sun)) * 0.32 * sunVisibility;
    specular = uSunColor * pow(max(0.0, dot(normal, normalize(sun + view))), 48.0) * 0.22 * sunVisibility;
    for (int index = 0; index < ${LIGHTING_CONFIG.maximumLocalLights}; index++) {
      if (index >= uLightCount) break;
      vec3 delta = uLightPositionRadius[index].xyz - vWorld;
      float distanceSquared = dot(delta, delta);
      float radius = uLightPositionRadius[index].w;
      if (distanceSquared >= radius * radius) continue;
      float range = max(0.0, 1.0 - distanceSquared / (radius * radius));
      vec3 direction = delta * inversesqrt(max(0.0001, distanceSquared));
      float cone = 1.0;
      if (uLightDirectionOuter[index].w > -0.5) cone = smoothstep(uLightDirectionOuter[index].w, uLightInnerCone[index], dot(-direction, uLightDirectionOuter[index].xyz));
      vec3 local = uLightColorIntensity[index].rgb * uLightColorIntensity[index].w * range * range * cone / (1.0 + distanceSquared * 0.2) * localLightVisibility(index, vWorld, normal);
      light += local * max(0.0, dot(normal, direction));
      if (uLocalSpecular > 0.5) specular += local * pow(max(0.0, dot(normal, normalize(direction + view))), 36.0) * 0.5;
    }
  }
  float fresnel = 0.035 + 0.60 * pow(1.0 - max(0.0, dot(view, normal)), 5.0);
  vec3 reflectedSky = tpsSkyColor(reflect(-view, normal));
  vec3 shaded = mix(body * light, reflectedSky, fresnel) + specular;
  float shore = (1.0 - smoothstep(0.05, 0.38, vDepthBank.x)) * smoothstep(0.005, 0.055, vDepthBank.x);
  float edgePattern = 0.5 + 0.5 * sin(vWorld.x * 3.1 + sin(vWorld.z * 2.3 - uTime * 0.8) + uTime * 0.65);
  float foam = clamp(shore * (0.13 + edgePattern * 0.13) + disturbed, 0.0, 0.45);
  shaded = mix(shaded, vec3(0.35, 0.39, 0.36) * light, foam);
  vec3 ray = vWorld - uCamera;
  float viewDepth = uTpsAtmosphere > 0.5 ? length(ray) : dot(ray, normalize(uViewForward));
  float fog = uFogEnabled > 0.5 ? smoothstep(uFogNear, uFogFar, viewDepth) * uFogMaximum : 0.0;
  if (fog > 0.001) {
    vec3 fogColor = uTpsAtmosphere > 0.5 ? tpsSkyColor(normalize(ray)) : uFogColor;
    shaded = mix(shaded, fogColor, fog);
  }
  float alpha = mix(0.62, 0.95, deep) * smoothstep(0.002, 0.075, vDepthBank.x);
  outColor = vec4(shaded, mix(alpha, 1.0, fog));
}`;

export const WATER_SPLASH_VERTEX_SOURCE = `#version 300 es
precision highp float;
uniform mat4 uViewProjection;
uniform vec4 uSplashes[${WATER_VISUAL_CONFIG.maximumSplashes}];
uniform float uSplashStrength[${WATER_VISUAL_CONFIG.maximumSplashes}];
uniform float uTime;
uniform float uPointScale;
uniform vec3 uCamera;
uniform float uFogNear;
uniform float uFogFar;
out float vAlpha;
void main() {
  int emitter = gl_VertexID / ${WATER_VISUAL_CONFIG.dropletsPerSplash};
  int droplet = gl_VertexID % ${WATER_VISUAL_CONFIG.dropletsPerSplash};
  vec4 splash = uSplashes[emitter];
  float strength = uSplashStrength[emitter];
  float age = uTime - splash.w;
  float seed = fract(sin(float(droplet * 17 + emitter * 13) + 0.4) * 43758.5453);
  float angle = float(droplet) * 2.399963;
  vec3 velocity = vec3(cos(angle) * (0.65 + seed), 1.8 + seed * 1.2, sin(angle) * (0.65 + seed)) * strength;
  vec3 world = splash.xyz + velocity * age + vec3(0.0, -4.9 * age * age + 0.035, 0.0);
  bool alive = age >= 0.0 && age < ${WATER_VISUAL_CONFIG.splashLifetime.toFixed(2)} && world.y > splash.y && strength > 0.0;
  float fog = smoothstep(uFogNear, uFogFar, length(world - uCamera));
  vAlpha = alive ? (1.0 - age / ${WATER_VISUAL_CONFIG.splashLifetime.toFixed(2)}) * 0.65 * (1.0 - fog) : 0.0;
  gl_PointSize = (2.2 + seed * 2.6) * uPointScale;
  gl_Position = alive ? uViewProjection * vec4(world, 1.0) : vec4(2.0, 2.0, 2.0, 1.0);
}`;

export const WATER_SPLASH_FRAGMENT_SOURCE = `#version 300 es
precision highp float;
in float vAlpha;
out vec4 outColor;
void main() {
  float distance = length(gl_PointCoord - vec2(0.5));
  if (distance > 0.5 || vAlpha <= 0.0) discard;
  outColor = vec4(0.43, 0.53, 0.52, vAlpha * (1.0 - smoothstep(0.2, 0.5, distance)));
}`;
